import type { ListeTableFilterData } from '@mairistem/liste';
import _ from 'lodash';

/**
 * Vérifie si les filtres de Liste donnés sont identiques.
 *
 * @param filterOne Le filtre à comparer.
 * @param filterTwo Le filtre avec lequel comparer.
 * @returns true si les filtres sont égaux, sinon false.
 */
export const isListeFilterEqual = (
  filterOne: ListeTableFilterData,
  filterTwo: ListeTableFilterData,
) => {
  const ignoredProperties = ['predicate'];
  if (_.isEqual(_.omit(filterOne, ignoredProperties), _.omit(filterTwo, ignoredProperties))) {
    return true;
  }

  return false;
};

/**
 * Vérifie si les filtres de Liste sont identiques (l'ordre compte).
 *
 * @param filters Les filtres à comparer.
 * @param filtersToCompareTo Les filtres avec lesquels comparer.
 * @returns true si les filtres sont égaux, sinon false.
 */
export const areListeFiltersEqual = (
  filters: ListeTableFilterData[],
  filtersToCompareTo: ListeTableFilterData[],
) => {
  // Si les longueurs sont différentes, les tableaux de filtres ne sont pas égaux
  if (_.size(filters) !== _.size(filtersToCompareTo)) {
    return false;
  }

  return filters.every((filter, index) => isListeFilterEqual(filter, filtersToCompareTo[index]));
};

/**
 * Construit les props de filtre par défaut d'une colonne de Liste à partir des filtres
 * mémorisés (issus du contexte), afin que la liste — non contrôlée — réaffiche le filtre
 * saisi par l'utilisateur (données + indicateur visuel) à son montage.
 *
 * @param name Le nom de la colonne (= propertyName du filtre).
 * @param memorizedFilters Les filtres actuellement mémorisés.
 * @returns Les props `filtersDefaultActive`/`filtersDefaultValues`, ou {} si aucun filtre mémorisé.
 */
export const getColumnFilterDefaults = (
  name: string,
  memorizedFilters?: ListeTableFilterData[],
) => {
  const filter = _.find(memorizedFilters, { propertyName: name });

  if (!filter) {
    return {};
  }

  return {
    filtersDefaultActive: filter.operator,
    filtersDefaultValues: { [filter.operator]: filter.value },
  };
};
